import axios from "axios";
import InsufficientCreditsException from "@/exceptions/InsufficientCreditsException";
import {SubtitleBurnInJob, SubtitleBurnInJobStatus} from "@/types";
import {createProgressListener, ProgressListener} from "@/utils/progressListener";
import ProgressSmoother, {ProgressSmootherConfig} from "@/utils/ProgressSmoother";
import {triggerDownload} from "@/utils/fileUtils";
import {VideoDimensions} from "@/Pages/Subtitle/Partials/VideoPlayer";

const burnInSmoothingConfig: ProgressSmootherConfig = {
    maxValue: 100,
    averageTimeBetweenValues: 10_000,
    averageValueIncreaseDelta: 3,
    delayUntilFirstValue: 20_000,
    firstValue: 3,
};

function makeBurnInSmoothingConfig(started_at?: number): ProgressSmootherConfig {
    return {...burnInSmoothingConfig, started_at};
}

export type BurnInJobListenerConfig = {
    jobId: string;
    updatesPerSecond?: number;
    startedAt?: number;
    onUpdate: (progress: number) => void;
    onError: (error: Error) => void;
    onComplete?: () => void;
};

export function listenForBurnInJobUpdates(config: BurnInJobListenerConfig): ProgressListener {
    const smoothingConfig = makeBurnInSmoothingConfig(config.startedAt);
    const progressSmoother = new ProgressSmoother(smoothingConfig);

    return createProgressListener({
        jobId: config.jobId,
        progressSmoother,
        updatesPerSecond: config.updatesPerSecond ?? 2,
        waitForRunning: true,
        requestInitialProgress: true,
        onProgress: config.onUpdate,
        onError: config.onError,
        onComplete: config.onComplete,
    });
}

export async function burnInSubtitle(
    subtitleId: number
): Promise<SubtitleBurnInJob> {
    try {
        const res= await axios.post(route('burn-in.create', { subtitle: subtitleId }));
        return res.data;
    } catch (error: any) {
        if (error.response) {
            const data = error.response.data;
            if (data?.type === "insufficient_credits") {
                throw new InsufficientCreditsException(data.balance)
            }
            const msg = data.message ? data.message : JSON.stringify(data);
            throw new Error(msg);
        } else {
            throw error;
        }
    }

}

export async function cancelBurnInJob(
    job: SubtitleBurnInJob
): Promise<void> {
    try {
        return await axios.put(route('burn-in.cancel', job.id));
    } catch (err: any) {
        throw new Error(err.response?.data?.message || err.message);
    }
}

export async function downloadOutput(jobId: number) {
    try {
        const res = await axios.get(
            route('burn-in.download', jobId)
        );
        triggerDownload(res.data.url)
    } catch (err: any) {
        throw new Error(err.response?.data?.message || err.message);
    }
}
